// src/services/deliveryCapture.ts
// -------------------------------
// Verified-address model (docs/delivery-address-verification-plan.md), Batch 3.
//
// resolveDeliveryForSubmit decides, at request submit, what the request's
// delivery columns should be — and, for a genuine one-off address the shelter
// entered via the Google autocomplete, CREATES (or reuses) a structured,
// UNVERIFIED delivery_addresses row and links the request to it by id, instead of
// storing a flat delivery_address_override string that can't split on the Sales
// Order.
//
// Branches:
//   1. Registered match  → { id, override:null } (unchanged behaviour).
//   2. One-off WITH structured parts (picked from autocomplete) → dedupe against
//      the shelter's existing rows; reuse a match, else insert a new row with
//      zephyr_verified = false (db-99 requires false for a shelter insert). The
//      row's id links the request; no override text.
//   3. One-off WITHOUT parts (freehand typed, or no shelter id) → keep the flat
//      override text (old behaviour). Rare; a responder can structure it later.
//   4. Any create failure → fall back to the flat override text. NEVER block the
//      submit because of address capture.

import { insertAddressReturning, listAllByShelterId } from "../data/deliveryAddresses";
import { resolveDeliveryForSave } from "../utils/helpers";

export interface DeliveryParts {
  line1?: string | null;
  line2?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  // Human label for the one-off address (ShelterDeliverySelect's AddressCaptureModal —
  // the shelter names it, prefilled "Delivery N"). When present it becomes the created
  // row's address_name; when absent we fall back to the next sequential nextDeliveryName.
  name?: string | null;
}

export interface DeliveryForSubmit {
  delivery_address_id: string | null;
  delivery_address_override: string | null;
}

interface AddrRowLike {
  id?: string;
  address_name?: string | null;
  street?: string | null;
  street2?: string | null;
  suburb?: string | null;
  suburb_city?: string | null;
  state?: string | null;
  postcode?: string | null;
  deactivated?: boolean | null;
}

function norm(s: unknown): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

// Dedupe key over the displayed locality (suburb_city, falling back to suburb).
function partsKey(p: DeliveryParts): string {
  return [p.line1, p.line2, p.suburb, p.state, p.postcode].map(norm).join("|");
}
function rowKey(r: AddrRowLike): string {
  return [r.street, r.street2, r.suburb_city || r.suburb, r.state, r.postcode].map(norm).join("|");
}

// The next sequential "Delivery N" label for a shelter, given its existing address
// names. Used when no human label is provided (the request-side one-off capture, and
// the admin modal when Address Name is left blank) — a unique, non-blank, non-street
// name that reads cleanly and satisfies Unleashed's AddressName. Scans existing
// "Delivery N" names (any case/spacing) and returns the next integer.
export function nextDeliveryName(existingNames: (string | null | undefined)[]): string {
  let max = 0;
  for (const n of existingNames) {
    const m = /^delivery\s+(\d+)$/i.exec(String(n ?? "").trim());
    if (m) {
      const v = Number.parseInt(m[1], 10);
      if (v > max) max = v;
    }
  }
  return `Delivery ${max + 1}`;
}

export async function resolveDeliveryForSubmit(args: {
  deliveryAddress?: string | null;
  deliveryParts?: DeliveryParts | null;
  shelterId?: string | null;
  deliveryAddresses?: AddrRowLike[] | null;
}): Promise<DeliveryForSubmit> {
  const { deliveryAddress, deliveryParts, shelterId, deliveryAddresses } = args;

  // 1. Registered match (existing sync logic).
  const base = resolveDeliveryForSave(deliveryAddress, deliveryAddresses);
  if (base.id) return { delivery_address_id: base.id, delivery_address_override: null };

  // 3. No structured parts (freehand) or no shelter → keep the flat text.
  const hasParts =
    !!deliveryParts &&
    !!(
      deliveryParts.line1 ||
      deliveryParts.suburb ||
      deliveryParts.state ||
      deliveryParts.postcode
    );
  if (!hasParts || !shelterId) {
    return { delivery_address_id: null, delivery_address_override: base.override };
  }

  // 2. One-off with parts → dedupe, else create an UNVERIFIED structured row.
  try {
    const existing = (await listAllByShelterId(shelterId)) || [];
    const key = partsKey(deliveryParts as DeliveryParts);
    const match = (existing as AddrRowLike[]).find((r) => !r.deactivated && rowKey(r) === key);
    if (match?.id) return { delivery_address_id: match.id, delivery_address_override: null };

    const row = {
      shelter_id: shelterId,
      // The shelter's own label (from the AddressCaptureModal, prefilled "Delivery N"),
      // else the next sequential "Delivery N" (older capture path / no name provided).
      // A non-blank, non-street human label that also satisfies Unleashed's AddressName.
      address_name:
        (deliveryParts as DeliveryParts).name?.trim() ||
        nextDeliveryName((existing as AddrRowLike[]).map((r) => r.address_name)),
      street: (deliveryParts as DeliveryParts).line1 || "",
      street2: (deliveryParts as DeliveryParts).line2 || null,
      suburb_city: (deliveryParts as DeliveryParts).suburb || null,
      state: (deliveryParts as DeliveryParts).state || null,
      postcode: (deliveryParts as DeliveryParts).postcode || null,
      zephyr_verified: false,
    };
    const ins = await insertAddressReturning(row);
    if (ins.error || !ins.data?.id) {
      // Create failed for any reason — never block the submit; keep the flat override.
      return { delivery_address_id: null, delivery_address_override: base.override };
    }
    return { delivery_address_id: ins.data.id, delivery_address_override: null };
  } catch {
    return { delivery_address_id: null, delivery_address_override: base.override };
  }
}
